import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Download, Share, PlusSquare, CheckCircle2, Smartphone, MoreVertical, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { canInstall, subscribeInstall, promptInstall, isStandalone, isIOS } from '@/lib/pwa';

// Manual "Install app" control for the Settings page. Uses the shared PWA
// singleton so it works even though the browser only fires beforeinstallprompt
// once, and falls back to iOS "Add to Home Screen" instructions.
export function InstallAppButton() {
  const [ready, setReady] = useState(canInstall());
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => subscribeInstall(() => {
    setReady(canInstall());
    setInstalled(isStandalone());
  }), []);

  const onClick = async () => {
    if (canInstall()) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') { setInstalled(true); toast.success('App installed'); }
    } else if (isIOS()) {
      setIosOpen(true);
    } else {
      setHelpOpen(true);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="install-app-panel">
      <div className="mb-1 flex items-center gap-2"><Smartphone className="h-4 w-4" /><p className="text-sm font-semibold">Install app</p></div>
      {installed ? (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm" data-testid="install-app-installed">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span>App is installed and running in standalone mode.</span>
        </div>
      ) : (
        <>
          <p className="mb-3 text-xs text-muted-foreground">Add SHABZ CLOSET to your home screen for one-tap access — full-screen, with its own icon, no browser bar.</p>
          <Button className="h-11 w-full rounded-xl" onClick={onClick} data-testid="install-app-btn">
            <Download className="mr-1.5 h-4 w-4" /> Download / Install app
          </Button>
          {!ready && !isIOS() && (
            <p className="mt-2 text-[11px] text-muted-foreground">Just installed the update? Refresh once, then tap again — or use your browser menu to &quot;Install app&quot;.</p>
          )}
        </>
      )}

      <AlertDialog open={iosOpen} onOpenChange={setIosOpen}>
        <AlertDialogContent data-testid="install-ios-help">
          <AlertDialogHeader>
            <AlertDialogTitle>Add to Home Screen</AlertDialogTitle>
            <AlertDialogDescription>Install SHABZ CLOSET on your iPhone/iPad in two taps.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 rounded-xl border border-border bg-secondary/40 p-3 text-sm">
            <p className="flex items-center gap-2"><Share className="h-4 w-4 text-primary" /> 1. Tap the <b>Share</b> button in Safari</p>
            <p className="flex items-center gap-2"><PlusSquare className="h-4 w-4 text-primary" /> 2. Choose <b>Add to Home Screen</b></p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="install-ios-close">Got it</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={helpOpen} onOpenChange={setHelpOpen}>
        <AlertDialogContent data-testid="install-generic-help">
          <AlertDialogHeader>
            <AlertDialogTitle>Install SHABZ CLOSET</AlertDialogTitle>
            <AlertDialogDescription>Your browser will add the app in a couple of taps.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-xl border border-border bg-secondary/40 p-3">
              <p className="mb-1 flex items-center gap-2 font-medium"><Smartphone className="h-4 w-4 text-primary" /> On Android (Chrome)</p>
              <p className="flex items-center gap-2 text-muted-foreground"><MoreVertical className="h-4 w-4" /> Tap the menu, then <b className="text-foreground">&nbsp;Add to Home screen / Install app</b></p>
            </div>
            <div className="rounded-xl border border-border bg-secondary/40 p-3">
              <p className="mb-1 flex items-center gap-2 font-medium"><Monitor className="h-4 w-4 text-primary" /> On desktop (Chrome / Edge)</p>
              <p className="flex items-center gap-2 text-muted-foreground"><Download className="h-4 w-4" /> Click the install icon in the address bar, or menu &rarr; <b className="text-foreground">&nbsp;Install SHABZ CLOSET</b></p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="install-generic-close">Got it</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
